import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Play, Pause, Volume2, VolumeX, ArrowRight, RotateCcw, Lock } from "lucide-react";
import { LOGO, VIDEO_SRC, VIDEO_POSTER } from "../mock";
import { track, makeVideoTracker } from "../lib/analytics";
import { utmQueryString } from "../lib/attribution";

const fmt = (s) => {
  const m = Math.floor(s / 60);
  const r = Math.floor(s % 60);
  return `${m}:${r < 10 ? "0" : ""}${r}`;
};

const WatchPage = () => {
  const navigate = useNavigate();
  const videoRef = useRef(null);
  const lastTime = useRef(0);
  const vt = useRef(makeVideoTracker({ title: "Full Stack Explainer (Watch page)", id: "explainer_watch" })).current;
  const [started, setStarted] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(false);
  const [ended, setEnded] = useState(false);
  const [progress, setProgress] = useState(0);
  const [remaining, setRemaining] = useState(0);

  useEffect(() => {
    document.title = "Watch First | Sassy Full Stack Fitness";
    window.scrollTo(0, 0);
  }, []);

  const start = async () => {
    const v = videoRef.current;
    if (!v) return;
    setStarted(true);
    track("watch_video_click", { location: "watch_page" });
    try {
      await v.play();
    } catch (e) {}
  };

  const toggle = async () => {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) {
      try {
        await v.play();
      } catch (e) {}
    } else v.pause();
  };

  const toggleMute = () => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = !v.muted;
    setMuted(v.muted);
  };

  // Locked playback: any attempt to jump snaps back to where they were.
  const onSeeking = (e) => {
    const v = e.currentTarget;
    if (Math.abs(v.currentTime - lastTime.current) > 0.75) v.currentTime = lastTime.current;
  };

  const onTimeUpdate = (e) => {
    const v = e.currentTarget;
    vt.onTimeUpdate(e);
    if (!v.seeking) lastTime.current = v.currentTime;
    if (v.duration) {
      setProgress((v.currentTime / v.duration) * 100);
      setRemaining(v.duration - v.currentTime);
    }
  };

  const onEnded = (e) => {
    vt.onEnded(e);
    setPlaying(false);
    setEnded(true);
  };

  const replay = async () => {
    const v = videoRef.current;
    setEnded(false);
    lastTime.current = 0;
    if (v) {
      v.currentTime = 0;
      try {
        await v.play();
      } catch (e) {}
    }
  };

  const goMatrix = () => {
    track("take_matrix_click", { location: "watch_page" });
    navigate(`/matrix${utmQueryString()}`);
  };

  return (
    <div className="bg-background min-h-screen flex flex-col" data-testid="watch-page">
      <header className="w-[90%] max-w-[1400px] mx-auto flex items-center justify-center h-[72px]">
        <img src={LOGO} alt="Sassy Full Stack Fitness" className="h-11 md:h-14 w-auto object-contain" />
      </header>

      <main className="flex-1 pb-16 md:pb-24">
        <div className="w-[90%] max-w-[980px] mx-auto text-center">
          <span className="inline-block text-[11px] font-bold tracking-[0.35em] uppercase text-primary mb-3 md:mb-4">
            Watch First
          </span>
          <h1 className="font-display font-black uppercase tracking-tighter leading-[0.9] text-3xl md:text-5xl text-white">
            <span className="text-gold-gradient block">Full Stack Coaching</span>
            <span className="block">See how it works</span>
          </h1>
          <p className="mt-3 md:mt-5 text-base md:text-lg text-white/70 font-medium max-w-2xl mx-auto">
            Four minutes with Sandra on the seven systems, and why treating them together is the
            difference between spinning your wheels and getting your body back.
          </p>

          <div
            className="relative mt-6 md:mt-8 rounded-[1.75rem] overflow-hidden border-[3px] border-primary/70 shadow-[0_0_0_1px_rgba(212,175,55,0.25),0_18px_60px_-15px_rgba(212,175,55,0.35)] bg-black"
            data-testid="watch-player"
          >
            <div className={ended ? "hidden" : "relative aspect-video w-full"}>
              <video
                ref={videoRef}
                src={VIDEO_SRC}
                poster={VIDEO_POSTER}
                playsInline
                preload="auto"
                disablePictureInPicture
                controlsList="nodownload noplaybackrate nofullscreen"
                onContextMenu={(e) => e.preventDefault()}
                onClick={started ? toggle : undefined}
                onPlay={(e) => {
                  vt.onPlay(e);
                  setPlaying(true);
                }}
                onPause={() => setPlaying(false)}
                onSeeking={onSeeking}
                onTimeUpdate={onTimeUpdate}
                onEnded={onEnded}
                className="absolute inset-0 w-full h-full bg-black"
                data-testid="watch-video"
              />

              {!started && (
                <button
                  onClick={start}
                  aria-label="Play the video"
                  data-testid="watch-play-button"
                  className="group absolute inset-0 z-10 flex flex-col items-center justify-center bg-gradient-to-b from-black/70 via-black/60 to-black/85 focus-ring"
                >
                  <span className="relative flex items-center justify-center w-16 h-16 md:w-20 md:h-20 rounded-full bg-primary text-primary-foreground shadow-[0_12px_40px_-6px_rgba(212,175,55,0.7)] group-hover:scale-110 transition-transform duration-300">
                    <span className="absolute inset-0 rounded-full bg-primary/40 animate-ping" />
                    <Play size={28} className="relative ml-1 fill-current" />
                  </span>
                  <span className="mt-3 md:mt-4 text-[11px] md:text-[12px] font-bold tracking-[0.28em] uppercase text-white">
                    Watch Video Now
                  </span>
                  <span className="mt-2 text-[11px] md:text-xs text-white/60 font-medium">Tap to play with sound</span>
                </button>
              )}

              {started && (
                <div className="absolute inset-x-0 bottom-0 z-10 flex items-center gap-3 px-3 md:px-4 py-2.5 bg-gradient-to-t from-black/85 to-transparent">
                  <button
                    onClick={toggle}
                    aria-label={playing ? "Pause" : "Play"}
                    data-testid="watch-toggle"
                    className="w-9 h-9 rounded-full bg-primary text-primary-foreground flex items-center justify-center hover:brightness-110 focus-ring"
                  >
                    {playing ? <Pause size={16} className="fill-current" /> : <Play size={16} className="ml-0.5 fill-current" />}
                  </button>
                  <div className="flex-1 h-1.5 rounded-full bg-white/20 overflow-hidden" aria-hidden="true" data-testid="watch-progress">
                    <div className="h-full bg-primary transition-[width] duration-200" style={{ width: `${progress}%` }} />
                  </div>
                  <span className="text-xs font-semibold text-white/80 tabular-nums" data-testid="watch-remaining">
                    -{fmt(remaining)}
                  </span>
                  <button
                    onClick={toggleMute}
                    aria-label={muted ? "Unmute" : "Mute"}
                    data-testid="watch-mute"
                    className="w-9 h-9 rounded-full bg-white/10 text-white flex items-center justify-center hover:bg-white/20 focus-ring"
                  >
                    {muted ? <VolumeX size={16} /> : <Volume2 size={16} />}
                  </button>
                </div>
              )}
            </div>

            {ended && (
              <div className="w-full text-center px-5 sm:px-6 py-12 sm:py-16 flex flex-col items-center bg-black fade-up" data-testid="watch-endcard">
                <span className="text-[10px] sm:text-[11px] font-bold tracking-[0.35em] uppercase text-primary mb-3 sm:mb-4">
                  Your Turn
                </span>
                <h2 className="font-display font-black uppercase tracking-tighter leading-[1.0] text-2xl sm:text-3xl md:text-4xl text-white max-w-2xl">
                  Take the Menopause Matrix<sup className="align-super text-[0.55em] font-semibold">™</sup> now
                </h2>
                <p className="mt-3 sm:mt-4 text-sm sm:text-lg text-white/75 font-medium max-w-xl leading-snug">
                  See which of your seven systems may be failing you. It takes about 90 seconds.
                </p>
                <div className="mt-6 sm:mt-8 flex flex-col sm:flex-row items-center gap-3 sm:gap-4 w-full sm:w-auto">
                  <button
                    onClick={goMatrix}
                    data-testid="watch-take-matrix"
                    className="w-full sm:w-auto justify-center inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground font-bold text-sm sm:text-base px-6 sm:px-8 py-3.5 sm:py-4 hover:brightness-110 active:scale-[0.98] transition focus-ring shadow-[0_12px_40px_-8px_rgba(212,175,55,0.6)]"
                  >
                    Take the Matrix (Free) <ArrowRight size={18} />
                  </button>
                  <button
                    onClick={replay}
                    data-testid="watch-replay"
                    className="inline-flex items-center gap-2 text-white/70 hover:text-white font-semibold transition-colors focus-ring rounded-md px-3 py-2 text-sm sm:text-base"
                  >
                    <RotateCcw size={16} /> Watch again
                  </button>
                </div>
              </div>
            )}
          </div>

          {!ended && (
            <p className="mt-6 inline-flex items-center gap-2 text-sm text-white/55 font-medium" data-testid="watch-unlock-note">
              <Lock size={14} className="text-primary" /> Your Menopause Matrix unlocks when the video ends.
            </p>
          )}
        </div>
      </main>

      <footer className="border-t border-white/10 py-6">
        <div className="w-[90%] max-w-[1400px] mx-auto flex flex-col sm:flex-row items-center justify-center gap-2 sm:gap-6 text-xs text-white/50">
          <span>© {new Date().getFullYear()} Sassy Full Stack Fitness</span>
          <a href="/privacy" className="hover:text-white/80" data-testid="watch-footer-privacy">Privacy Policy</a>
          <a href="/terms" className="hover:text-white/80" data-testid="watch-footer-terms">Terms</a>
        </div>
      </footer>
    </div>
  );
};

export default WatchPage;
